import { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import GroupLanding from "./GroupLanding";
import InstructorDetails from "./InstructorDetails";
import PageLoader, { Spinner } from "./PageLoader";
import { toast } from "react-toastify";
// One answer for "can this member open this group yet", shared with the
// groups page so the two screens cannot drift apart.
import { groupLevelGate, groupLevelLabel, isLevelGroup, isCourseLinkedGroup } from "./groupLevelGate";
import axios from "axios";
import config from "./config";
import { cachedPost } from "./apiCache";
import { nativeLogin } from "./nativeLogin";
import { fetchProfile } from "./nativeProfile";
import MyPlansModal from "./MyPlansModal";
import MembershipHeader, { BrandLogo, CompactNavMenu } from "./MembershipHeader";
// Commented out alongside the switcher's render site further down (search this
// file for "Membership switcher"). Restore both together.
// import MembershipSwitcher from "./MembershipSwitcher";
import Tooltip from "./Tooltip";
import { SectionHeader } from "./MembershipViews";
import { NAV_TABS, COMMUNITY_NAV_KEYS, COMMUNITY_MORE_KEYS, GROUP_SCOPED_PAGES, homeEnabled, landingSection, sectionPath, START_PAGE, customTabs, isCustomKey, customIdOf, orderTabs, splitInline } from "./nav";
import MembershipSidebar from "./MembershipSidebar";
import EmptyState from "./EmptyState";
import ProfileModal from "./ProfileModal";
import ChangePasswordModal from "./ChangePasswordModal";
import AccountSettingsModal from "./AccountSettingsModal";
import MobileCodeModal from "./MobileCodeModal";
import AuthModal from "./AuthModal";
import applyCommunityFont, { applyCommunityDirection, applyCommunityTheme } from "./applyFont";
import { injectCustomScript } from "./injectCustomScript";
import { setFavicon, DEFAULT_FAVICON } from "./favicon";
import { useBrand } from "./useBrand";
import MembershipShell from "./MembershipShell";
import Pagination from "./Pagination";
import CoursesView, { CommunityMiniBanner } from "./CourseViews";
import MockTestLauncher from "./MockTestLauncher";
import ChatModal from "./ChatModal";
import { MembersView, LeaderboardView, RewardsView, EventsView, MyPointsCard, useMyPoints, UpcomingEventBanner } from "./MembershipViews";
import PolicyFooter from "./PolicyFooter";
import BannerPopup from "./BannerPopup";
import InlineBanner from "./InlineBanner";
import WelcomeFlowers from "./WelcomeFlowers";
import { instructorsOf, InstructorChips } from "./instructors";
import SpotlightBanner, { spotlightBannerFor } from "./spotlight/SpotlightBanner";
import { SpotlightIntro, SpotlightExplore, SPOTLIGHT_GRID } from "./spotlight/SpotlightCourses";
import { BannerIcon } from "./spotlight/bannerIcons";
import profileBanner from "./profileBanner";
import { withReferral } from "./affiliateReferral";

// Professional default cover for group cards that have no banner of their own.
const GROUP_DEFAULT_BANNER =
  "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1200&q=80";

// The backend hands back these "empty" placeholders when a group has no real
// banner — treat them as missing so the GROUP_DEFAULT_BANNER above is used.
const isEmptyBanner = (url) =>
  !url || /\/(signin-empty|logo-empty)\.png(\?|$)/i.test(url);
const groupBanner = (url) => (isEmptyBanner(url) ? GROUP_DEFAULT_BANNER : url);

const EMPTY_FORM = { name: "", email: "", phone: "", password: "", otp: "", password_confirmation: "", gdpr_consent: false };

// Paid groups (access_type 3) send the buyer here to complete the purchase.
const PAID_CHECKOUT_URL = "https://app.flexifunnel.com";

/*
 * Where "Unlock" sends a member for a gated group — the product the bridge says
 * unlocks it, rather than a hardcoded host that has no idea which group was
 * clicked. Mirrors GroupCollection; kept in both because these two screens do
 * not share a module today.
 */
const unlockUrlFor = (g) => {
  /*
   * The bridge now hands back the product's real published page as
   * `sales_url`, resolved the same way a course card's is (its sales page in
   * either form, else its checkout). Use it verbatim — it is the page the
   * creator configured and the one the group's own landing sells through.
   *
   * What it replaces never worked. The host below is missing the "s" of
   * flexifunnels, and /checkout/{product_code} is not a route that app serves
   * either way, so "Get access" on every paid group opened a dead page. Kept
   * only as the last resort for a bridge response that predates `sales_url`.
   */
  const url = g?.unlock_product?.sales_url;
  if (url) return url;

  const code = g?.unlock_product?.code;
  return code ? `${PAID_CHECKOUT_URL}/checkout/${code}` : PAID_CHECKOUT_URL;
};

// "$12" for whole amounts, "$12.50" otherwise.
const formatPrice = (amt) => {
  const n = Number(amt) || 0;
  return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
};


// How many group cards to show per page in the community group list.
const GROUPS_PER_PAGE = 12;

const GROUP_PAGES = ["home", "profile", "password", "plans", "earnings", "search", "page", "feed", "courses", "events", "members", "map", "leaderboard", "rewards", "memberchat", "about", "pendingpost"];

// Community-home section slug → banner placement key (matches the admin Banner
// Ads "Show on" options). The group list is the "Community" placement.
const HOME_PLACEMENT = {
  courses: "courses",
  groups: "community",
  members: "members",
  map: "map",
  events: "events",
  leaderboard: "leaderboard",
  rewards: "rewards",
};

const memberKey = (token) => `cm_${token}`;
function readMember(token) {
  try {
    return JSON.parse(localStorage.getItem(memberKey(token)) || "null");
  } catch {
    return null;
  }
}

/**
 * Community group collection page.
 *
 * /:token -> loads the community banner + all groups. A signed-in member can
 * click any group to join it (community_group_members). Auth (Join / Sign in /
 * Forgot password) lives in one interlinked modal.
 */
function MembershipHome() {
  // Section-slug routing: /membership/:token/:section (courses | feed | groups |
  // events | members | map | leaderboard | rewards). The group is implicit — the
  // active group (set via the switcher) or the member's primary joined group.
  const { token, section } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?gid={group_token} carries the active group. On a group section it selects
  // which group's feed/courses to show (so the URL is reloadable/shareable); on
  // the community home it opens that group's PUBLIC landing page instead.
  const gidParam = searchParams.get("gid");
  const refCode = searchParams.get("ref"); // ?ref={referrer url_token} -> referral link
  const focusPost = searchParams.get("post"); // ?post={post_id} -> deep-link to one post
  const focusComment = searchParams.get("comment"); // ?comment={comment_id} -> scroll to a comment/reply
  // The instructor page is addressed by ?instructor={id} rather than held in
  // page state, so a refresh — or a shared link, or the browser's Back button —
  // keeps it. ?ifrom=course records that the visitor arrived from a course,
  // which is what decides whether that page leads with courses or groups.
  const instructorParam = searchParams.get("instructor");
  const instructorView = /^\d+$/.test(instructorParam || "") ? instructorParam : null;
  const instructorFocus = searchParams.get("ifrom") === "course" ? "course" : "group";
  // The active group for slug sections; null = use the member's primary group.
  const [activeGroupToken, setActiveGroupToken] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [community, setCommunity] = useState(null);
  const [groups, setGroups] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  // The active group's detail, and the token the SERVER chose for it. Both come
  // from the groups request now; the shell used to fetch the first for itself.
  const [activeGroup, setActiveGroup] = useState(null);
  const [serverPrimary, setServerPrimary] = useState(null);

  // Signed-in community member (persisted per community).
  const [member, setMember] = useState(() => readMember(token));
  const memberId = member?.community_member_id;

  // Memberships the signed-in learner belongs to — powers the header membership
  // switcher, only when the owner enabled it (Brand → Options).
  const [myMemberships, setMyMemberships] = useState([]);
  const switcherOn = Number(community?.membership_switcher) === 1;
  // Project-wide nav menu visibility (Settings → Membership Menus). 1 = hide.
  // Keys mirror TAB_MENU_KEY in MembershipShell; the community home applies the
  // membership-level flags only (per-group flags apply inside a group).
  const communityMenus = community?.menus || {};
  const HOME_TAB_MENU_KEY = {
    home: "home",
    courses: "course",
    groups: "community",
    events: "event",
    members: "member",
    map: "map",
    leaderboard: "leaderboard",
    rewards: "reward",
    memberchat: "memberchat",
  };
  const isNavHidden = (key) => {
    // Home is opt-in: hidden unless explicitly switched on (see homeEnabled).
    if (key === "home") return !homeEnabled(communityMenus);
    const mk = HOME_TAB_MENU_KEY[key];
    return !!mk && Number(communityMenus[mk]) === 1;
  };
  // Standard + custom menus, in the order the creator dragged them into
  // (community.menu_order); without one, the standard tabs then the custom ones.
  const availableNavTabs = orderTabs([...NAV_TABS.filter((t) => !isNavHidden(t.key)), ...customTabs(community)], community?.menu_order);
  // What the community pages' navs list (Feed lives inside a group), and the
  // header templates' split of it into bar tabs and "More ▾".
  const communityNavTabs = availableNavTabs.filter(
    (t) => COMMUNITY_NAV_KEYS.includes(t.key) || COMMUNITY_MORE_KEYS.includes(t.key) || t.custom
  );
  const [communityBarTabs, communityMoreTabs] = splitInline(communityNavTabs, COMMUNITY_NAV_KEYS);
  useEffect(() => {
    if (!member || !switcherOn) { setMyMemberships([]); return undefined; }
    let on = true;
    axios
      .post(`${config.COMMUNITY_API_URL}/membership-my-memberships`, {
        community_token: token,
        member_token: member.member_token,
      })
      .then((res) => { if (on && res.data?.error === 0) setMyMemberships(res.data.memberships || []); })
      .catch(() => { /* switcher just stays empty */ });
    return () => { on = false; };
  }, [member, switcherOn, token]);

  const [showProfile, setShowProfile] = useState(false);
  const [showMyPlans, setShowMyPlans] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showMobileCode, setShowMobileCode] = useState(false);

  // Group-join UI state
  const [groupBusyId, setGroupBusyId] = useState(null);
  const [pendingGroup, setPendingGroup] = useState(null);
  const [notice, setNotice] = useState("");

  // Group-join confirmation modal (opens when a non-joined group card is clicked)
  const [joinGroup, setJoinGroup] = useState(null);
  const [joinMsg, setJoinMsg] = useState("");


  // Opening / closing the instructor page is a URL change, not a state change —
  // see the ?instructor= reads above. setSearchParams pushes a history entry, so
  // the browser's own Back button works the same as the page's Back button.
  const openInstructor = (instructorId, focus = "group") => {
    if (!instructorId) return;
    const next = new URLSearchParams(searchParams);
    next.set("instructor", String(instructorId));
    // Only the non-default origin needs recording, so arriving from a group
    // leaves the URL as short as it was.
    if (focus === "course") next.set("ifrom", "course");
    else next.delete("ifrom");
    setSearchParams(next);
  };
  const closeInstructor = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("instructor");
    next.delete("ifrom");
    setSearchParams(next);
  };

  // Community home always shows the group list now that members/events/etc. are
  // reached via their own section slugs (kept as a const for the render below).
  const view = "groups";

  // Group list search
  const [groupQuery, setGroupQuery] = useState("");
  // Catalog renders its course search up in the header bar (like the group
  // search). CoursesView portals its <CourseSearch> into this slot node.
  const [courseSearchSlot, setCourseSearchSlot] = useState(null);
  // Campus group sections (Continue / Explore more) show the first few, with a
  // "Show more" in the section header once a section has 4+ groups.
  const [campusContinueAll, setCampusContinueAll] = useState(false);
  const [campusExploreAll, setCampusExploreAll] = useState(false);
  const CAMPUS_GROUP_LIMIT = 4;
  // Creator's left sidebar can collapse to an icons-only rail (persisted). The
  // page's left padding tracks it, so the state lives here (the parent).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("creatorSidebarCollapsed") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => setSidebarCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem("creatorSidebarCollapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });

  // Group list pagination — show GROUPS_PER_PAGE cards per page.
  const [groupPage, setGroupPage] = useState(1);

  // Discover filter popover: Type (visibility) is chosen first, then Price.
  // "all" means that facet is not constraining the list.
  const [filterOpen, setFilterOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all"); // all | public | private
  const [priceFilter, setPriceFilter] = useState("all"); // all | free | paid | free_trial

  // Auth modal: null | "join" | "signin" | "forgot"
  const [modal, setModal] = useState(null);
  // One-shot welcome flower shower shown right after a successful signup.
  const [welcome, setWelcome] = useState({ show: false, name: "" });
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" }); // type: "error" | "success"
  // Forgot-password OTP flow: step 1 email → 2 code → 3 new password.
  const [forgotStep, setForgotStep] = useState(1);
  const [resetToken, setResetToken] = useState("");

  // Theme primary comes from the SAME source as the login page — getfunnel →
  // brand_settings.theme_color1 (see useBrand) — NOT the membership-groups
  // community payload, so header/cards/buttons match the login page exactly.
  // Light shades are derived at each use site via alpha-hex (e.g. `${primary}14`).
  const { primary, brand } = useBrand(token);
  const myGroups = groups.filter((g) => g.joined);
  const discoverGroups = groups.filter((g) => !g.joined);
  // Joined groups first, then discover — each section ordered by group creation
  // date (ascending). `groups` is pre-sorted that way, and filter() preserves
  // that order, so both partitions come out date-ascending.
  const orderedGroups = member ? [...myGroups, ...discoverGroups] : groups;

  // ----- Section-slug routing -----
  // Group-scoped sections render inside a group (MembershipShell); "groups" (or no
  // section) is the community home. GROUP_PAGES is the set of in-group sections.
  const isGroupSection = !!section && GROUP_PAGES.includes(section);
  // The active group for group-scoped sections: the URL's ?gid wins (so the feed
  // page is reloadable/shareable), then the switcher's choice, then the member's
  // primary joined group, else the first listed group.
  // The server's choice wins when it made one — it is the token `activeGroup`
  // was built for, so deriving a different one here would scope the page to one
  // group while showing another's detail. The local derivation stays as the
  // fallback for a response that predates primary_group_token.
  const primaryGroupToken = serverPrimary || (myGroups[0] || groups[0])?.group_token || null;
  const groupToken = (isGroupSection && gidParam) || activeGroupToken || primaryGroupToken;
  // On the community home, ?gid instead opens the group's PUBLIC landing page.
  const landingGid = isGroupSection ? null : gidParam;
  const gid = isGroupSection ? groupToken : null;
  // Membership-wide sections (Events, Map, Members, Courses, …) render in the
  // shell even when this membership has no groups at all — only the truly
  // group-scoped pages (Feed / About / Pending) need a real group to open. Without
  // this, a groupless membership silently bounced every one of those pages back to
  // the (empty) group list.
  const groupRequired = GROUP_SCOPED_PAGES.includes(section);
  // In-group sections that are NOT group-scoped (Courses, Members, Events,
  // Rewards, Leaderboard, Map) always render through MembershipShell — the
  // `inGroupPage` test below cannot be false for them — and the shell draws no
  // group cards; its only group-list consumer is the feed's GroupSwitcher, and
  // the feed is group-scoped so it is excluded here. Both halves come straight
  // from the URL's `section`, so this never changes mid-load.
  const slimGroupList = isGroupSection && !groupRequired;
  // Only two screens read the group list: the community home (the group cards)
  // and the feed (its group switcher). Everywhere else it was ~2KB and two
  // queries of nothing — the group this page is scoped to now arrives as
  // active_group, so the list is not needed to find it either.
  // /membership-details is the same response without it.
  const needsGroupList = !isGroupSection || section === "feed";
  const inGroupPage = isGroupSection && (!!gid || !groupRequired);
  // "My Points" standing for the right-side card on the Members section (mirrors
  // the Courses page layout, which pairs the community banner with this card).
  // `refresh` re-pulls the standing right after a point-earning action (e.g. the
  // profile modal's first photo upload) so the My Points card updates on the
  // spot instead of only after a page reload.
  // This hook stays mounted while MembershipShell renders the active section
  // BELOW it, so it has to agree with the shell about whether the board is on
  // screen — otherwise the two ask for different bodies on the Leaderboard
  // section and the tab makes an extra request instead of sharing one. `view`
  // is pinned to "groups" here, so the route's `section` is what decides.
  const { me: myStanding, pts: myPointsSettings, refresh: refreshMyPoints } = useMyPoints(token, gid, member, section === "leaderboard", !loading);
  // Navigate to a section slug. Group-scoped sections (Feed) carry ?gid={group_token}
  // so the URL captures which group is open; community-wide membership sections
  // (Courses, Events, Members, …) omit it. A referral code is preserved when present.
  const goSection = (s, gt = groupToken) => {
    // A custom menu ("page:<id>") opens its page, addressed by ?id.
    if (isCustomKey(s)) {
      const p = new URLSearchParams({ id: customIdOf(s) });
      if (refCode) p.set("ref", refCode);
      navigate(`/membership/${token}/page?${p.toString()}`);
      return;
    }
    const params = new URLSearchParams();
    if (GROUP_SCOPED_PAGES.includes(s) && gt) params.set("gid", gt);
    if (refCode) params.set("ref", refCode);
    const qs = params.toString();
    navigate(`/membership/${token}/${s}${qs ? `?${qs}` : ""}`);
  };
  // Open the Members section pre-filtered to a group (Groups filter + that group
  // selected). Used by the group card's "N Members" link.
  const goMembers = (gt) => {
    const params = new URLSearchParams();
    if (gt) params.set("group", gt);
    if (refCode) params.set("ref", refCode);
    const qs = params.toString();
    navigate(`/membership/${token}/members${qs ? `?${qs}` : ""}`);
  };
  const q = groupQuery.trim().toLowerCase();
  // access_type 2 = private; anything else (1) is public.
  const matchType = (g) =>
    typeFilter === "all" ||
    (typeFilter === "private" ? g.access_type === 2 : g.access_type !== 2);
  const matchPrice = (g) =>
    priceFilter === "all" || (g.pricing_type || "free") === priceFilter;
  const matchSearch = (g) =>
    !q ||
    (g.name || "").toLowerCase().includes(q) ||
    (g.description || "").toLowerCase().includes(q);
  const visibleGroups = orderedGroups.filter(
    (g) => matchSearch(g) && matchType(g) && matchPrice(g)
  );
  const activeFilters = (typeFilter !== "all" ? 1 : 0) + (priceFilter !== "all" ? 1 : 0);

  // Pagination — slice the filtered list into pages of GROUPS_PER_PAGE.
  const totalGroupPages = Math.max(1, Math.ceil(visibleGroups.length / GROUPS_PER_PAGE));
  // Keep the page in range when the list shrinks (search / filter changes).
  const safeGroupPage = Math.min(groupPage, totalGroupPages);
  const pagedGroups = visibleGroups.slice(
    (safeGroupPage - 1) * GROUPS_PER_PAGE,
    safeGroupPage * GROUPS_PER_PAGE
  );
  // Reset to the first page whenever the search query or filters change.
  useEffect(() => {
    setGroupPage(1);
  }, [groupQuery, typeFilter, priceFilter, view]);
  const saveMember = (m) => {
    setMember(m);
    try {
      localStorage.setItem(memberKey(token), JSON.stringify(m));
    } catch {
      /* ignore */
    }
  };
  const signOut = () => {
    setMember(null);
    try {
      localStorage.removeItem(memberKey(token));
    } catch {
      /* ignore */
    }
    setGroups((gs) => gs.map((g) => ({ ...g, joined: false })));
    // Logging out returns to the login page (not the community group list).
    navigate(`/membership/${token}/login`);
  };

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await cachedPost(`${config.COMMUNITY_API_URL}/membership-details`, {
        community_token: token,
        community_member_id: memberId || undefined,
        // The Privacy/Terms bodies are ~28KB — 85% of this response — for a
        // footer that shows two links. Ask for presence flags instead; the
        // footer fetches the text itself if a member opens one.
        skip_policies: 1,
        // Drop the per-group instructor lookup and member counts on the sections
        // that never draw a group card. This endpoint is still needed there — it
        // resolves which group the page is scoped to, and carries the theme —
        // but those two fields cost two queries for nothing.
        //
        // The gate is derived from the URL alone, deliberately. Anything that
        // depended on `groups` (the primary group token, say) would flip once
        // this very response landed and refetch the endpoint a second time, and
        // anything looser would let a card render from a slimmed list with no
        // member count and no instructor chips. See slimGroupList.
        ...(slimGroupList ? { slim_groups: 1 } : {}),
        // Ask for the group this page is scoped to on the SAME request. The
        // shell needs that group's detail, and it could not name the group until
        // this response arrived — so it used to come back for it in a second
        // call. `?gid` names it when the URL carries one; otherwise the server
        // picks the primary by the same rule as primaryGroupToken below and
        // reports its choice in primary_group_token.
        ...(isGroupSection
          ? (gidParam ? { active_group_token: gidParam } : { with_active_group: 1 })
          : {}),
      }, { ttl: 8000 });
      if (res.data?.error === 0) {
        setCommunity(res.data.community);
        applyCommunityFont(res.data.community?.membership_font);
        applyCommunityDirection(res.data.community?.rtl);
        applyCommunityTheme(res.data.community?.brand);
        // Creator's retargeting / analytics pixels (admin → Settings → General →
        // General Code). Keyed by the membership PROJECT id — the funnels row
        // (membership_mode = 1) that `/customscript` looks up — never the URL
        // token and never a `communities` id. Self-guarded against duplicate
        // injection and never throws.
        injectCustomScript(res.data.community?.funnel_id);
        // Show groups oldest-first (created date ascending); group_id breaks ties.
        // The details response never carries `groups` — the list comes from its
        // own request below. Kept defensive so an older server that still sends
        // one is used rather than ignored.
        if (Array.isArray(res.data.groups)) setGroups(
          (res.data.groups || []).slice().sort(
            (a, b) =>
              (new Date(a.created_at || 0) - new Date(b.created_at || 0)) ||
              ((Number(a.group_id) || 0) - (Number(b.group_id) || 0))
          )
        );
        setTotalMembers(res.data.total_members || 0);
        setActiveGroup(res.data.active_group ?? null);
        setServerPrimary(res.data.primary_group_token ?? null);
      } else {
        setError(res.data?.msg || "Unable to load this membership.");
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Something went wrong while loading the membership.");
    } finally {
      setLoading(false);
    }
    // slimGroupList belongs here: navigating from a slimmed section to one that
    // draws group cards has to re-read the list, or the cards render with no
    // member count and no instructor chips. Different body, different cachedPost
    // key, so it is a real refetch and not a cache hit.
  }, [token, memberId, slimGroupList, isGroupSection, gidParam]);

  useEffect(() => {
    if (token) loadGroups();
  }, [token, loadGroups]);

  // The group list, separately, and only on the two screens that draw it — the
  // community home's cards and the feed's group switcher. Every other page now
  // bootstraps identically off /membership-details, which means one cached
  // response serves them all instead of each tab shape refetching its own.
  const loadGroupList = useCallback(async () => {
    if (!token || !needsGroupList) return;
    try {
      const res = await cachedPost(`${config.COMMUNITY_API_URL}/membership-groups`, {
        community_token: token,
        community_member_id: memberId || undefined,
        list_only: 1,
      }, { ttl: 8000 });
      if (res.data?.error === 0 && Array.isArray(res.data.groups)) {
        setGroups(
          res.data.groups.slice().sort(
            (a, b) =>
              (new Date(a.created_at || 0) - new Date(b.created_at || 0)) ||
              ((Number(a.group_id) || 0) - (Number(b.group_id) || 0))
          )
        );
      }
    } catch {
      // The list is one section of one screen; a failure here must not take
      // down a page whose membership loaded fine.
    }
  }, [token, memberId, needsGroupList]);

  useEffect(() => {
    loadGroupList();
  }, [loadGroupList]);

  // A signed-in member who has already joined a group should land directly on
  // its Feed rather than on its public landing page when they open a
  // /:token?gid=... link (shared link, bookmark, new tab). Expired free-trials
  // are excluded — they still need the landing page to renew (see GroupCard's
  // trialLapsed rule).
  const landingGroup = landingGid
    ? groups.find((g) => g.group_token === landingGid)
    : null;
  const enterJoinedLanding =
    !!member &&
    !!landingGroup?.joined &&
    !(landingGroup.pricing_type === "free_trial" && landingGroup.trial_expired);
  useEffect(() => {
    if (!enterJoinedLanding || !landingGroup) return;
    setActiveGroupToken(landingGroup.group_token);
    goSection("feed", landingGroup.group_token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterJoinedLanding, landingGroup]);

  // Reload the group list when returning from a group page to the collection,
  // so joined/left changes — and a sign-out done inside the feed — are reflected
  // with the now-settled member state (no stale reload racing the logout).
  // /home is where every sign-in lands, before anything knows whether this
  // membership has the Home dashboard switched on. Once the membership has
  // loaded and it turns out to be off, continue to Courses — replacing the
  // entry, so Back doesn't bounce the member through the redirect again.
  //
  // Sign-ins now land on /start, which always forwards to the Default page.
  // So does any page the creator has hidden in Settings → Membership Menus
  // (Home when the dashboard is off, Courses, Events, …) — a bookmark or a
  // refresh on it must not keep showing a page that's no longer in the nav.
  // Not when the URL opens it inside a group (?gid=): there the group's own
  // menu settings decide. (`gid` itself is no use here — it falls back to the
  // member's last/primary group even with no ?gid in the URL.)
  // `landing !== section` guards a loop should the fallback itself be hidden.
  const landing = community ? landingSection(community.menus) : null;
  const forwardTo = !community
    ? null
    : section === START_PAGE || (!gidParam && isNavHidden(section) && landing !== section)
      ? landing
      : null;
  useEffect(() => {
    if (!forwardTo) return;
    navigate(sectionPath(token, forwardTo, refCode), { replace: true });
  }, [forwardTo, navigate, token, refCode]);

  // The layout template on <html>, so template-scoped theme tokens (Spotlight's
  // warm palette) reach everything — including the modals and menus that
  // portal to <body>, outside any page root. Cleared on the way out so the
  // login page never inherits a membership's palette.
  const pageTemplate = community?.template || "";
  useEffect(() => {
    if (!pageTemplate) return undefined;
    const root = document.documentElement;
    root.setAttribute("data-tpl", pageTemplate);
    return () => {
      if (root.getAttribute("data-tpl") === pageTemplate) root.removeAttribute("data-tpl");
    };
  }, [pageTemplate]);

  const wasInGroupPage = useRef(inGroupPage);
  useEffect(() => {
    if (wasInGroupPage.current && !inGroupPage) loadGroups();
    wasInGroupPage.current = inGroupPage;
  }, [inGroupPage, loadGroups]);

  // Tab favicon: the community's uploaded favicon if it has one, else the
  // bundled FlexiLogo default.
  useEffect(() => {
    setFavicon(community?.favicon || DEFAULT_FAVICON);
  }, [community?.favicon]);

  // Tab title: the community name (falls back to FlexiMemberships) — without it
  // the browser shows the raw URL (e.g. "localhost:3000") in the tab.
  useEffect(() => {
    document.title = community?.name ? community.name : "FlexiMemberships";
  }, [community?.name]);

  // Keep the avatar/profile button in sync with the saved profile photo.
  useEffect(() => {
    if (!memberId) return;
    let active = true;
    fetchProfile({ funnelId: member?.funnel_id, memberId, jwt: member?.member_token })
      .then((d) => ({ data: d }))
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        const fresh = res.data.member;
        if (fresh && (fresh.img !== member?.img || fresh.name !== member?.name)) {
          saveMember({ ...member, name: fresh.name, email: fresh.email, img: fresh.img });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, memberId]);

  const openModal = (name) => {
    // Sign In no longer opens a popup — redirect to the dedicated login page
    // (covers session-out, explicit "Log In", and modal switches to sign in).
    if (name === "signin") {
      navigate(`/membership/${token}/login`);
      return;
    }
    setMsg({ type: "", text: "" });
    setForm(EMPTY_FORM);
    setForgotStep(1);
    setResetToken("");
    setModal(name);
  };
  const closeModal = () => !busy && setModal(null);

  // Guests may browse membership pages without a session — no auto sign-in
  // prompt on mount. Signing in is explicit (the "Log In" action redirects to
  // the dedicated /membership/:token/login page).

  // ---- Join a specific group (insert into community_group_members) ----
  const doGroupJoin = useCallback(
    async (g) => {
      setGroupBusyId(g.group_id);
      setNotice("");
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-group-join`, {
          community_token: token,
          community_member_id: memberId,
          member_token: member?.auth_token,
          group_token: g.group_token,
        });
        if (res.data?.error === 0) {
          setGroups((prev) =>
            prev.map((x) =>
              x.group_id === g.group_id
                ? {
                    ...x,
                    joined: !!res.data.joined,
                    pending: !!res.data.pending,
                    member_count: res.data.member_count ?? x.member_count,
                  }
                : x
            )
          );
          if (res.data.pending) {
            const m = res.data.msg || "Request sent for admin approval.";
            setNotice(m);
            toast.info(m);
          } else if (res.data.joined) {
            toast.success(`You've joined ${g.name}! 🎉`);
            // Welcome email for the joiner. The join itself is handled by the
            // bridge, which cannot reach the membership app's mail code, so the
            // browser raises it — exactly as it already does for comments,
            // replies and likes. Only on a real join, never on a pending
            // request. Fire-and-forget: the member is already in the group, so a
            // mail failure must not surface as a join error.
            axios
              .post(`${config.MEMBERSHIP_API_URL}/membership-event-notify`, {
                membership_token: token,
                type: "welcome",
                group_id: g.group_id,
                member_id: memberId,
              })
              .catch(() => {});
          }
        } else {
          const m = res.data?.msg || "Could not join the community.";
          setNotice(m);
          toast.error(m);
        }
        return res.data;
      } catch (err) {
        const m = err.response?.data?.msg || "Could not join the community.";
        setNotice(m);
        toast.error(m);
        return { error: 1, msg: m };
      } finally {
        setGroupBusyId(null);
      }
    },
    [token, memberId, member?.auth_token]
  );

  // Entering a group lands on the Feed tab (the group's discussion home).
  const openGroup = (g) => {
    setActiveGroupToken(g.group_token);
    // The feed is the group's home now that it's no longer a navbar tab —
    // entering a group lands on its feed (reachable only from inside a group).
    goSection("feed", g.group_token);
  };

  // Clicking a group card opens that group's feed inline on this same page
  // (MembershipShell renders in place). Guests browse read-only — any write action
  // prompts Sign In, and private groups are gated server-side — so non-members
  // can enter the feed too; the feed surfaces Join / access prompts inside.
  const onGroupClick = (g) => {
    if (!g?.group_token) return;
    // Same restriction as the groups page: an unearned level group says which
    // level rather than opening a feed the bridge refuses.
    const levelMsg = groupLevelGate(g);
    if (levelMsg) {
      toast.info(levelMsg);
      return;
    }
    // Joined → straight to the group's feed. Not joined → the group's landing
    // page, where "Join group" joins and then redirects into the feed.
    if (g.joined) {
      openGroup(g);
      return;
    }
    navigate(`/membership/${token}/groups?gid=${g.group_token}`);
  };

  // Confirm join from the group-join modal: a free group opens its feed right
  // away; a private group sends an approval request and shows the result inline.
  const confirmGroupJoin = async () => {
    const g = joinGroup;
    if (!g) return;
    if (!member) {
      // Not signed in yet — ask them to join/sign in, then auto-join this group.
      setJoinGroup(null);
      setPendingGroup(g);
      openModal("join");
      return;
    }
    setJoinMsg("");
    const data = await doGroupJoin(g);
    if (data?.error === 0 && data.joined) {
      setJoinGroup(null);
      openGroup(g);
    } else if (data?.error === 0 && data.pending) {
      setJoinMsg(data.msg || "Request sent for admin approval.");
    } else {
      setJoinMsg(data?.msg || "Could not join the community.");
    }
  };

  // After auth completes (modal closed + member set), auto-join the pending
  // group and open it on its feed (free groups) so the member lands inside.
  useEffect(() => {
    if (member && pendingGroup && !modal) {
      const g = pendingGroup;
      setPendingGroup(null);
      // Paid groups require payment after login — keep the member on the landing
      // page (where "Get access" now opens checkout), don't auto-join/enter.
      if ((g.pricing_type || "free") === "paid") return;
      doGroupJoin(g).then((data) => {
        if (data?.error === 0 && data.joined) {
          setActiveGroupToken(g.group_token);
          goSection("feed", g.group_token);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, pendingGroup, modal, doGroupJoin, token]);

  // Group pages (feed/members/leaderboard/rewards) are browseable by guests:
  // the content renders read-only and any write action (post/comment/like) or
  // restricted header action opens the Sign In popup inside MembershipShell.
  // Private groups are still protected server-side (membership-feed returns an
  // access error that MembershipShell surfaces), so no client-side gate here.

  const submitJoin = async (e) => {
    e.preventDefault();
    // GDPR: block signup if consent is mandatory and not given.
    if (community?.gdpr?.enabled && community?.gdpr?.mandatory && !form.gdpr_consent) {
      return setMsg({ type: "error", text: "Please accept the Privacy and Terms to continue." });
    }
    // Phone: block signup if the community made the phone number mandatory.
    if (Number(community?.phone_required) === 1 && !String(form.phone || "").replace(/\D/g, "")) {
      return setMsg({ type: "error", text: "Please enter your phone number to continue." });
    }
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-join`, {
        community_token: token,
        ref: new URLSearchParams(window.location.search).get("ref") || undefined,
        ...form,
      });
      if (res.data?.error === 0) {
        saveMember(res.data.member);
        setTotalMembers((n) => n + 1);
        setModal(null);
        setForm(EMPTY_FORM);
        // New customer onboarding: celebrate with a flower shower.
        setWelcome({ show: true, name: res.data.member?.name || "" });
      } else {
        setMsg({ type: "error", text: res.data?.msg || "Could not join." });
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      // Native membership login (api/login); funnel_id = the community id.
      const m = await nativeLogin({
        funnelId: community?.community_id,
        email: form.email,
        password: form.password,
      });
      saveMember(m);
      setMsg({ type: "success", text: `Welcome back, ${m.name || ""}!`.trim() });
      // Continue into the membership's landing page (Home when it's switched
      // on, else Courses), not the group list the modal was opened from — the
      // same place the other two sign-in entries (/membership/:token/login and
      // the /membership/:token redirect) land.
      setModal(null);
      setForm(EMPTY_FORM);
      goSection(landingSection(community?.menus));
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || err.message || "Invalid email or password." });
    } finally {
      setBusy(false);
    }
  };

  // 3-step OTP reset: send code → verify code → set new password.
  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      if (forgotStep === 1) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-send-otp`, {
          community_token: token,
          email: form.email,
        });
        if (res.data?.success) {
          setResetToken(res.data.token || "");
          // Local dev convenience: the API echoes the OTP back so we can prefill it.
          setForgotStep(2);
        } else {
          setMsg({ type: "error", text: res.data?.message || "Could not send the code." });
        }
      } else if (forgotStep === 2) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-verify-otp`, {
          community_token: token,
          email: form.email,
          otp: form.otp,
          token: resetToken,
        });
        if (res.data?.success) {
          setForgotStep(3);
        } else {
          setMsg({ type: "error", text: res.data?.message || "Invalid code." });
        }
      } else {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-reset-password`, {
          community_token: token,
          email: form.email,
          otp: form.otp,
          token: resetToken,
          password: form.password,
          password_confirmation: form.password_confirmation,
        });
        if (res.data?.success) {
          setForgotStep(1);
          setMsg({ type: "success", text: res.data?.message || "Your password has been reset." });
        } else {
          setMsg({ type: "error", text: res.data?.message || "Could not reset your password." });
        }
      }
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || err.response?.data?.msg || "Something went wrong. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  // While /start (or a Home that's off) forwards, keep the loader up rather
  // than flashing the group list for a frame.
  if (loading || forwardTo || section === START_PAGE) {
    return (
      <div className="min-h-screen bg-canvas">
        <PageLoader
          page={{ home: "Home", search: "Search", page: "Page", profile: "Profile", password: "Password", plans: "My Plans", earnings: "Earnings", courses: "Courses", groups: "Community", events: "Events", members: "Members", map: "Map", leaderboard: "Leaderboard", rewards: "Rewards", memberchat: "Members Chat" }[section] || "Community"}
          logo={community?.logo || undefined}
          primary={primary}
        />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
        <p className="text-red-500 text-center">{error}</p>
      </div>
    );
  }

  // Instructor details page — ?instructor={id} (see the param reads above).
  if (instructorView) {
    return (
      <>
        <InstructorDetails
          token={token}
          instructorId={instructorView}
          member={member}
          focus={instructorFocus}
          onBack={closeInstructor}
          onLogin={() => openModal("signin")}
          // VIEW on a hosted group enters that group on its feed (the group home).
          onView={(g) => {
            setActiveGroupToken(g.group_token);
            goSection("feed", g.group_token);
          }}
          // VIEW on a course opens it on its lesson list. goSection() can't carry
          // these — the course is addressed by query string, not by section slug.
          onViewCourse={(c) => {
            const params = new URLSearchParams({ course: c.course_token, view: "lessons" });
            if (refCode) params.set("ref", refCode);
            navigate(`/membership/${token}/courses?${params.toString()}`);
          }}
        />

        {modal && (
          <AuthModal
            mode={modal}
            community={community}
            primary={primary}
            form={form}
            setForm={setForm}
            busy={busy}
            msg={msg}
            onClose={closeModal}
            onSwitch={openModal}
            onSubmitJoin={submitJoin}
            onSubmitSignin={submitSignin}
            forgotStep={forgotStep}
            onSubmitForgot={submitForgot}
          />
        )}
        <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />
      </>
    );
  }

  // Group pages open inline on the same /:token page (no separate route): the
  // active section is driven by ?page=, so the header highlights it and the
  // URL is shareable. Guests can browse too — MembershipShell renders read-only
  // and prompts Sign In on any write/restricted action.
  if (inGroupPage) {
    return (
      <MembershipShell
        token={token}
        groupToken={gid}
        refCode={refCode}
        focusPostId={focusPost}
        focusCommentId={focusComment}
        // Mirror member changes (sign in / sign out done inside the feed) into
        // this page's state so the community view reflects them without a refresh.
        onMemberChange={(m) => (m ? saveMember(m) : signOut())}
        // Referral links hide the "Community" tab so the referred user stays
        // focused on the group they were invited to.
        navTabs={refCode ? availableNavTabs.filter((t) => t.key !== "groups") : availableNavTabs}
        page={section}
        // The shell used to re-fetch the community object and the member count
        // from /membership-feed. Both are already here, from /membership-groups,
        // and that response is a superset of what the feed returned.
        community={community}
        totalMembers={totalMembers}
        activeGroup={activeGroup}
        // `gid` above is null until the groups list lands. Passed on so the
        // shell's My Points card waits for the same moment this one does,
        // rather than firing a throwaway request against a null group.
        groupReady={!loading}
        // Section nav -> path slug (/membership/:token/:section).
        onChangePage={(p) => goSection(p)}
        policies={community?.policies}
        communityName={community?.name}
        template={community?.template || "classic"}
        // Group switcher in the in-group header: switching the active group keeps
        // the current section in the URL; the view re-renders for the new group.
        myGroups={myGroups}
        onSwitchGroup={(gt) => {
          setActiveGroupToken(gt);
          goSection(section, gt);
        }}
        onDiscoverGroups={() => goSection("groups")}
        // Membership switcher (Courses subView). The Courses page routes through
        // this shell whenever the membership has groups, so the switcher must live
        // here too — not only in MembershipHome's own header.
        membershipSwitcherOn={switcherOn}
        myMemberships={myMemberships}
        onSwitchMembership={(t) => navigate(`/membership/${t}`)}
        // Leave the group and return to the community group list.
        onNavigate={() => goSection("groups")}
        // Clicking an instructor (a group host on the About tab, a course's
        // instructor on the Courses tab) opens their page. The section in the URL
        // is left ALONE: ?instructor={id} is added on top of it and the instructor
        // page renders instead of this shell, so Back just drops the param and
        // returns to whichever section they came from. This used to navigate to
        // ?page=groups first, which is why Back from a course always landed on
        // Community.
        onInstructor={openInstructor}
        onBack={() => goSection("groups")}
      />
    );
  }

  // Group landing page (public) opens at /:token?gid={group_token}
  if (landingGid) {
    // Already-joined members are being redirected into the group by the effect
    // above — show the loading screen rather than flashing the landing page.
    if (enterJoinedLanding) {
      return (
        <div className="min-h-screen bg-canvas">
          <PageLoader page="Community" primary={primary} />
        </div>
      );
    }
    return (
      <>
        <GroupLanding
          token={token}
          gid={landingGid}
          member={member}
          onBack={() => navigate(`/${token}`)}
          // Header "Log In" (guests): open the Sign In popup, stay on the
          // landing page. After auth the pendingGroup effect lands them in the
          // group feed.
          onLogin={(g) => {
            setPendingGroup(g);
            openModal("signin");
          }}
          onJoin={(g) => {
            const pricing = g.pricing_type || "free";
            // Guests sign up / sign in first, then land back on this same page.
            if (!member) {
              setPendingGroup(g);
              openModal("join");
              return;
            }
            /*
             * Gated → the offer that unlocks it. Driven by the bridge's `locked`,
             * with the pricing check kept only as a fallback for an older bridge:
             * price and entry are separate axes now.
             */
            const gated =
              g.locked ?? (g.access_mode === "entitlement" || pricing === "paid");
            if (gated || (pricing === "free_trial" && g.joined && g.trial_expired)) {
              /*
               * Earned at a level: nothing to buy, so the card does not route
               * anywhere — the group's own landing states the level.
               */
              if (g.locked && (g.pricing_type === "level" || Number(g.unlock_level) > 0)) return;
              if (g.locked && !g.unlock_product) return; // invite-only: nothing to buy
              window.open(withReferral(unlockUrlFor(g)), "_blank", "noopener,noreferrer");
              return;
            }
            if (g.joined) {
              openGroup(g);
            } else {
              // Signed in but not a member yet — join, then enter on Courses.
              // RETURNED so the caller can await the round-trip: GroupLanding
              // re-reads the group afterwards to flip its CTA to "Request Sent",
              // and without the return it awaited an already-resolved undefined
              // and re-read BEFORE the request row existed.
              return doGroupJoin(g).then((data) => {
                if (data?.error === 0 && data.joined) {
                  openGroup(g);
                }
                return data;
              });
            }
          }}
          // Click "By <instructor>" -> that instructor's page (?instructor={id}).
          onInstructor={openInstructor}
        />

        {/* Auth modal — opened on demand by Log In / Join (not auto) */}
        {modal && (
          <AuthModal
            mode={modal}
            community={community}
            primary={primary}
            form={form}
            setForm={setForm}
            busy={busy}
            msg={msg}
            onClose={closeModal}
            onSwitch={openModal}
            onSubmitJoin={submitJoin}
            onSubmitSignin={submitSignin}
            forgotStep={forgotStep}
            onSubmitForgot={submitForgot}
          />
        )}
        <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />
      </>
    );
  }

  // Community-home navbar click → section slug. "groups" is the community home;
  // group-scoped tabs (Courses / Feed / …) open the implicit group on that page.
  const onCommunityNav = (key) => goSection(key);

  // Frontend layout template the owner picked in the admin (Settings → Templates),
  // delivered on the community payload. Only "classic" ships today; the other
  // layouts (studio / catalog / campus / creator) are marked "Soon" in the admin
  // picker and fall back to Classic until their shell lands (Phase 2). This is the
  // single switch point — add `if (template === "studio") return <StudioShell …/>`
  // branches here as each layout ships.
  const template = community?.template || "classic";
  // Studio layout (Settings → Templates): swaps the top-header chrome for a left
  // sidebar and drops the hero for an editorial heading. The shared view-switch
  // body and all modals below are reused verbatim — only the chrome/containers
  // branch on `isStudio`, so Classic stays byte-for-byte the same.
  const isStudio = template === "studio";
  const studioSectionInNav =
    COMMUNITY_NAV_KEYS.includes(section) || COMMUNITY_MORE_KEYS.includes(section);
  const studioTitle =
    NAV_TABS.find((t) => t.key === (studioSectionInNav ? section : "groups"))?.label || "Communities";

  // Catalog layout: search-forward top bar over a flat, paginated group grid
  // (joined groups first). No hero.
  const isCatalog = template === "catalog";
  const catalogHasQuery = !!q || activeFilters > 0;
  // The "N Members" pill links to the Members section only when that menu is
  // enabled in the admin (Settings → Membership Menus). Disabled → plain text.
  const membersMenuEnabled = !isNavHidden("members");
  const renderGroupCard = (g) => (
    <GroupCard
      key={g.group_id}
      g={g}
      primary={primary}
      busy={groupBusyId === g.group_id}
      onClick={() => onGroupClick(g)}
      onOpen={() => openGroup(g)}
      onInstructor={openInstructor}
      onMembers={membersMenuEnabled ? (gt) => goMembers(gt) : null}
    />
  );

  // Campus layout: content column with Continue / Explore-more group sections and
  // the same membership rail (community card + My Points) the Courses page uses.
  const isCampus = template === "campus";

  // Creator layout: mobile-first / app-like — app bar, banner+avatar hero, a
  // story rail of group circles, single-column feed, and a fixed bottom tab bar.
  const isCreator = template === "creator";
  // Spotlight: full-height collapsible sidebar, slim search bar, and a banner
  // heading every page.
  const isSpotlight = template === "spotlight";
  // Classic is the default membership layout: content + a right rail.
  const isClassic = !isStudio && !isCatalog && !isCampus && !isCreator && !isSpotlight;
  // Templates whose groups list is wrapped in the Courses-style card (header +
  // grid inside). Classic and Studio do; Catalog keeps its own grid.
  const groupsHasRail = isClassic || isStudio || isSpotlight;
  // Templates that get the right-hand membership rail (banner + My Points /
  // Levels / How to earn) beside the groups list. Catalog and Creator take one
  // too so their community page matches Courses/Events.
  const groupsRail = groupsHasRail || isCatalog || isCreator;

  // One logo everywhere. This used to be a second, slightly different copy of
  // BrandLogo (h-9/max-w-150/text-lg vs h-8/max-w-130/text-base), so the brand
  // visibly changed size between the group page and every other page.
  // The logo is a "home" affordance → the membership's landing page (Home when
  // it's switched on, else Courses).
  const communityLogo = <BrandLogo community={community} onClick={() => onCommunityNav(landingSection(community?.menus))} />;


  return (
    <div
      data-template={template}
      className={
        isSpotlight
          ? `isolate flex min-h-screen flex-col bg-canvas transition-[padding] duration-300 ${sidebarCollapsed ? "lg:ps-[72px]" : "lg:ps-64"}`
          : isStudio
          ? "min-h-screen bg-[#fbfbfc] dark:bg-canvas lg:ps-64"
          : isCatalog
            ? "min-h-screen flex flex-col bg-[#fbfbfc] dark:bg-canvas"
            : isCampus
              ? "min-h-screen flex flex-col bg-gray-50"
              : isCreator
                // No left padding on the root: the header spans full width (logo
                // always top-left) and only the content below is pushed beside the
                // sidebar. Mobile uses the header hamburger + drawer (no bottom nav).
                ? "min-h-screen flex flex-col bg-gray-50"
                : "min-h-screen flex flex-col bg-gradient-to-b from-[#fbfbfc] to-[#f3f4f6] dark:from-canvas dark:to-canvas"
      }
    >
      {/* ---------- Studio / Creator: fixed left sidebar (desktop only) ---------- */}
      {(isStudio || isCreator || isSpotlight) && (
        <MembershipSidebar
          logo={communityLogo}
          logoIcon={
            <span className="flex h-9 w-9 items-center justify-center rounded-xl text-sm font-extrabold text-white" style={{ backgroundColor: primary }}>
              {(community?.name || "?").charAt(0).toUpperCase()}
            </span>
          }
          primary={primary}
          tabs={communityNavTabs}
          activeKey={section}
          isActive={(key) => (studioSectionInNav ? section === key : key === "groups")}
          onNavigate={onCommunityNav}
          member={member}
          onJoin={() => openModal("join")}
          belowHeader={isCreator}
          collapsed={isCreator || isSpotlight ? sidebarCollapsed : false}
          onToggle={isCreator || isSpotlight ? toggleSidebar : undefined}
          variant={isSpotlight ? "spotlight" : "default"}
          footerItems={isSpotlight && member ? [{ key: "profile", label: "My Profile", icon: <BannerIcon name="profile" className="h-[18px] w-[18px]" />, onClick: () => goSection("profile") }] : []}
        />
      )}

      {/* Main column (Studio) / transparent passthrough (Classic via `contents`). */}
      {/* min-h-screen on the COLUMN, not just the root.
          Studio's root is a plain block (`lg:ps-64`, no flex), so `flex-1` here
          was inert and this column was only as tall as its content. The body
          below is `flex-1` and had no spare height to grow into, so the footer
          stopped directly under the content and the root's `min-h-screen` merely
          painted background beneath it — visible on any short page, e.g. a
          community with a single group. Making the column itself at least a
          viewport tall gives the body room to grow and pins the footer to the
          bottom. Independent of whether the root is flex, so nothing else moves. */}
      <div className={isStudio ? "flex min-w-0 flex-1 flex-col min-h-screen" : "contents"}>

      {/* One header for every template. The template decides the bar's look
          (Catalog's search row, Campus's bar, Creator's slim bar); this page only
          supplies data. The in-group shell renders the SAME component with the
          same template, so a section can't get a different header. */}
      <MembershipHeader
        template={template}
        community={community}
        token={token}
        member={member}
        primary={primary}
        tabs={communityBarTabs}
        moreItems={communityMoreTabs}
        activeKey={section}
        onNavigate={onCommunityNav}
        // Membership switcher (Brand → Options → "Membership switcher"). Sits in
        // the same header slot the group feed uses for the group switcher, so on
        // Membership switcher lives on the Courses page — the membership-first
        // landing — not on the Community (groups) page, which has its own group
        // switcher on a group's feed.
        switcher={
          /* Membership switcher — the small up/down icon button beside the logo
             — commented out on request. Kept, not deleted: restore by removing
             this comment wrapper and the one on the import at the top of this
             file.

          switcherOn && section === "courses" ? (
            <MembershipSwitcher
              memberships={myMemberships}
              currentToken={token}
              primary={primary}
              onSelect={(t) => navigate(`/membership/${t}`)}
            />
          ) : null
          */
          null
        }
        // A nav/more section highlights its own tab; anything else (e.g. an
        // in-group page like feed) falls back to Groups so the navbar never
        // shows a dangling selection.
        isActive={(key) =>
          COMMUNITY_NAV_KEYS.includes(section) || COMMUNITY_MORE_KEYS.includes(section)
            ? section === key
            : key === "groups"
        }
        navClass="hidden md:flex"
        // Studio's sidebar carries the brand (bar shows it only on mobile). Creator
        // keeps the logo in the full-width bar (its sidebar sits under the header).
        logoWrapClass={isStudio || isSpotlight ? "lg:hidden" : ""}
        // Same rule as the shell: a compact menu on narrow screens for every
        // template except Creator (which has a bottom nav). The header itself
        // ignores it where the nav already lives in a sub-row.
        mobileNav={
          !isCreator && !isSpotlight ? (
            <CompactNavMenu
              items={communityNavTabs}
              activeKey={section}
              isActive={(key) => (studioSectionInNav ? section === key : key === "groups")}
              onNavigate={onCommunityNav}
              primary={primary}
            />
          ) : null
        }
        // Catalog carries its search in the bar — the group search on the groups
        // landing, and (via a portal slot filled by CoursesView) the course search
        // on the Courses section. This page owns the group-search state.
        extra={
          !isCatalog ? null : section === "courses" ? (
            // Slot for CoursesView's <CourseSearch> portal.
            <div ref={setCourseSearchSlot} className="order-last w-full max-w-none sm:order-none sm:w-auto sm:max-w-xl sm:flex-1" />
          ) : (
            <div className="relative order-last w-full max-w-none sm:order-none sm:w-auto sm:max-w-xl sm:flex-1">
              <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-gray-400">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={groupQuery}
                onChange={(e) => setGroupQuery(e.target.value)}
                placeholder="Search communities"
                onFocus={(e) => {
                  e.target.style.borderColor = primary;
                  e.target.style.boxShadow = `0 0 0 3px ${primary}22`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "";
                  e.target.style.boxShadow = "";
                }}
                className="w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-11 pr-4 text-sm text-gray-700 placeholder-gray-400 outline-none transition-colors focus:bg-surface"
              />
            </div>
          )
        }
        // Logo → the membership's landing page (Home when on, else Courses).
        onLogoClick={() => onCommunityNav(landingSection(community?.menus))}
        // Spotlight opens these as pages (the shell renders them); the other
        // templates keep the popups.
        onProfile={isSpotlight ? () => goSection("profile") : () => setShowProfile(true)}
        onChangePassword={isSpotlight ? () => goSection("password") : () => setShowChangePassword(true)}
        onAccountSettings={() => setShowAccountSettings(true)}
        onMyPlans={isSpotlight ? () => goSection("plans") : () => setShowMyPlans(true)}
        onMobileCode={() => setShowMobileCode(true)}
        onSignOut={signOut}
        onSignIn={() => openModal("signin")}
        onJoin={() => openModal("join")}
      />

      {/* Creator: content sits beside the (below-header) sidebar; collapsing it
          widens only this column. Other templates pass through transparently. */}
      <div className={isCreator ? `flex min-w-0 flex-1 flex-col transition-[padding] duration-300 ${sidebarCollapsed ? "lg:ps-16" : "lg:ps-64"}` : "contents"}>

      {/* The Classic hero used to sit here. Its banner, name, description and
          member/group counts now live in the right rail below (matching Courses
          and Events), so a full-width hero on top would just repeat them. The
          guest "Join Community" CTA moved into that rail with them. */}

      {/* Spotlight heads the page with its banner (not over the full-height chat). */}
      {isSpotlight && section !== "memberchat" ? (
        <>
          <SpotlightBanner section={section || "groups"} subtitle={community?.name} primary={primary} {...spotlightBannerFor(brand, section || "groups")} />
        </>
      ) : null}

      {/* ---------- Active view (shared body — reused by every layout) ---------- */}
      <div
        className={
          isSpotlight
            ? "w-full flex-1 px-4 py-8 sm:px-6 lg:px-8"
            : isStudio
            // Same container as the shell uses for Courses/Events/Members, so the
            // content and the membership rail don't change width between sections.
            ? "mx-auto w-full max-w-7xl flex-1 px-4 py-8"
            : isCatalog
              ? "mx-auto w-full max-w-[1200px] px-4 py-8 sm:px-6"
              : isCampus
                ? "mx-auto w-full max-w-[1180px] px-4 py-8 sm:px-6"
                : isCreator
                  // Fills the width freed by the collapsible sidebar.
                  ? "w-full px-4 py-8 sm:px-6"
                  // Classic: same container as Courses / Events.
                  : "w-full max-w-7xl mx-auto px-4 py-8"
        }
      >
        {/* Studio's editorial page heading — only for sections that don't title
            themselves. The group list renders its own "Groups (n)" header and the
            sidebar already shows the community name, so on that view any heading
            here is a duplicate. */}
        {isStudio && studioSectionInNav && section !== "groups" && (
          <h2 className="mb-6 text-2xl font-bold tracking-tight text-gray-900">
            {studioTitle}
          </h2>
        )}
        {/* Inline banner ads for the community/group-list home on templates that
            have NO right rail (catalog / creator). Classic, Studio and Campus
            render the banner inside their rail instead. The view-based sections
            (courses/members/…) render their own rail banner. */}
        {(!section || section === "groups") && !groupsRail && !isCampus && (
          <InlineBanner token={token} placement="community" wrapperClass="mb-6 space-y-3" />
        )}
        {section === "courses" ? (
          // Courses are funnel-scoped (not group-scoped). This branch is only
          // reached when the community has no group to open (inGroupPage is
          // false), so the Courses tab still lists the funnel's courses here.
          <CoursesView pointsCard={<MyPointsCard ready={!loading} me={myStanding} pts={myPointsSettings} token={token} groupToken={null} primary={primary} member={member} />} onPointsChange={refreshMyPoints} token={token} groupToken={null} primary={primary} member={member} group={null} template={template} community={community} totalMembers={totalMembers} headerSearchSlot={isCatalog ? courseSearchSlot : null} onInstructor={(instructorId) => openInstructor(instructorId, "course")} />
        ) : section === "memberchat" ? (
          // Members Chat as a full page (fallback path when the membership has no
          // group to enter; the in-group shell renders the same section otherwise).
          member ? (
            // Chat is a full-height app surface: on phones it cancels this
            // container's gutters (px-4 + the template's py-6/py-8) so it runs
            // edge-to-edge under the 64px sticky header.
            <div
              className={`${isCreator ? "-mx-4 -my-6" : "-mx-4 -my-8"} h-[calc(100dvh-4rem)] sm:mx-0 sm:my-0 sm:h-[calc(100vh-9rem)] sm:min-h-[560px] lg:h-[calc(100vh-11rem)]`}
            >
              <ChatModal token={token} member={member} primary={primary} embedded />
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-surface p-10 text-center text-sm text-gray-500">
              Sign in to message other members.
            </div>
          )
        ) : view === "members" ? (
          <MembersView token={token} groupToken={gid} primary={primary} member={member} groupCard={<CommunityMiniBanner community={community} totalMembers={totalMembers} className="h-44" />} pointsCard={<MyPointsCard ready={!loading} me={myStanding} pts={myPointsSettings} token={token} groupToken={gid} primary={primary} member={member} />} />
        ) : view === "events" ? (
          <EventsView token={token} primary={primary} template={template} communityName={community?.name} />
        ) : view === "leaderboard" ? (
          <LeaderboardView token={token} primary={primary} member={member} />
        ) : view === "rewards" ? (
          <RewardsView token={token} primary={primary} member={member} />
        ) : isCampus ? (
          // Content left, the same membership rail the Courses page uses on the right.
          <div className="tpl-rail-grid">
            <div className="order-1 min-w-0">
              {notice && (
                <div className="mb-4 text-sm rounded-xl px-3.5 py-2.5 bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-500/30">
                  {notice}
                </div>
              )}
              {/* Events targeted to the Community page. */}
              <MockTestLauncher page="community" groupToken={null} funnelId={member?.funnel_id} memberId={member?.community_member_id} jwt={member?.member_token} primary={primary} />
              <UpcomingEventBanner token={token} placement="community" primary={primary} className="mb-6" />
              {groups.length === 0 ? (
                <div className="rounded-2xl border border-gray-200 bg-surface">
                  <EmptyState art="groups" accent={primary} title="No communities yet" message="Communities are the smaller rooms inside this membership - a place to gather around one topic, with their own posts and members. The first one to be created shows up here." />
                </div>
              ) : (() => {
                // One "Groups" card: search lives in the header; the body shows the
                // search results, or the Continue / Explore-more subsections (each
                // capped, with a "Show more" at the right end when it has 4+ groups).
                const sections = [
                  (member && myGroups.length > 0) && {
                    key: "continue", title: "Continue", subtitle: "Communities you've joined",
                    list: myGroups, all: campusContinueAll, setAll: setCampusContinueAll,
                  },
                  discoverGroups.length > 0 && {
                    key: "explore",
                    title: (member && myGroups.length > 0) ? "Explore more" : "Communities",
                    subtitle: "Communities in this membership",
                    list: discoverGroups, all: campusExploreAll, setAll: setCampusExploreAll,
                  },
                ].filter(Boolean);
                const groupsSearch = (
                  <div className="relative w-full sm:w-72">
                    <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-gray-400">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      value={groupQuery}
                      onChange={(e) => setGroupQuery(e.target.value)}
                      placeholder="Search communities"
                      onFocus={(e) => { e.target.style.borderColor = primary; e.target.style.boxShadow = `0 0 0 3px ${primary}22`; }}
                      onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
                      className="w-full rounded-xl border border-gray-200 bg-surface py-2.5 pl-10 pr-4 text-sm text-gray-700 placeholder-gray-400 outline-none transition-colors"
                    />
                  </div>
                );
                return (
                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-surface shadow-sm">
                    <SectionHeader
                      primary={primary}
                      iconPath="M4 5h6v6H4V5zm10 0h6v6h-6V5zM4 15h6v4H4v-4zm10 0h6v4h-6v-4z"
                      title="Communities"
                      subtitle="Communities in this membership"
                      action={groupsSearch}
                    />
                    {catalogHasQuery ? (
                      visibleGroups.length === 0 ? (
                        <EmptyState
                          art="search"
                          accent={primary}
                          title="No communities match"
                          message={groupQuery ? `Nothing here matches “${groupQuery}”. Try a shorter word, or clear the filters to see everything.` : "No community matches the filters you picked. Clear them to see everything in this membership."}
                          action={{
                            label: "Clear search & filters",
                            onClick: () => { setGroupQuery(""); setTypeFilter("all"); setPriceFilter("all"); },
                          }}
                        />
                      ) : (
                        <div className="p-5 sm:p-6">
                          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">{visibleGroups.map(renderGroupCard)}</div>
                        </div>
                      )
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {sections.map((sec) => {
                          const hasMore = sec.list.length > CAMPUS_GROUP_LIMIT;
                          const shown = sec.all ? sec.list : sec.list.slice(0, CAMPUS_GROUP_LIMIT);
                          return (
                            <div key={sec.key} className="p-5 sm:p-6">
                              {/* Subsection label + a right-aligned "Show more". */}
                              <div className="mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                                <div className="min-w-0">
                                  <h3 className="text-[15px] font-bold text-gray-900">{sec.title}</h3>
                                  <p className="mt-0.5 text-[12px] text-gray-500">{sec.subtitle}</p>
                                </div>
                                {hasMore && (
                                  <button
                                    type="button"
                                    onClick={() => sec.setAll((v) => !v)}
                                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold transition hover:brightness-95"
                                    style={{ backgroundColor: `${primary}14`, color: primary }}
                                  >
                                    {sec.all ? "Show less" : "Show more"}
                                    <svg className={`h-4 w-4 transition-transform ${sec.all ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">{shown.map(renderGroupCard)}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Right rail — matches the Courses page: community card + My Points. */}
            <aside className="order-2 space-y-4 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
              <CommunityMiniBanner
                community={community}
                totalMembers={totalMembers}
                totalGroups={groups.length}
                className="h-44"
              />
              {member ? (
                <MyPointsCard ready={!loading} me={myStanding} pts={myPointsSettings} token={token} groupToken={gid} primary={primary} member={member} />
              ) : (
                <button
                  onClick={() => openModal("join")}
                  style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                  className="w-full rounded-full px-7 py-3 font-bold text-white shadow-lg transition hover:opacity-90"
                >
                  Join Community
                </button>
              )}
              <InlineBanner token={token} placement="community" wrapperClass="space-y-3" />
            </aside>
          </div>
        ) : ((() => {
          // Search + filter. Sits in the card header on the templates that use the
          // card (Classic/Studio), and standalone above the list on the others.
          const groupsSearch = !isCatalog && !isCreator && groups.length > 0 ? (
            <div className={`flex items-center gap-3 ${groupsHasRail ? "w-full sm:w-auto" : isStudio ? "mb-8" : "mb-8 max-w-2xl mx-auto"}`}>
              <div className="relative flex-1 sm:w-72">
                <span className="absolute inset-y-0 left-3.5 flex items-center text-gray-400 pointer-events-none">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                </span>
                <input
                  type="text"
                  value={groupQuery}
                  onChange={(e) => setGroupQuery(e.target.value)}
                  placeholder="Search communities..."
                  onFocus={(e) => {
                    e.target.style.borderColor = primary;
                    e.target.style.boxShadow = `0 0 0 3px ${primary}22`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "";
                    e.target.style.boxShadow = "";
                  }}
                  className={`w-full rounded-xl border border-gray-200 bg-surface shadow-sm pl-10 pr-4 text-sm text-gray-700 placeholder-gray-400 outline-none focus:outline-none transition-colors ${groupsHasRail ? "py-2.5" : "py-3.5"}`}
                />
              </div>
              <GroupFilter
                primary={primary}
                open={filterOpen}
                setOpen={setFilterOpen}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                priceFilter={priceFilter}
                setPriceFilter={setPriceFilter}
                activeFilters={activeFilters}
              />
            </div>
          ) : null;

          const groupsList =
            groups.length === 0 ? (
              /* Nothing has been created yet — say what a group IS, since a
                 member seeing this has no example on screen to learn from. */
              <EmptyState
                art="groups"
                accent={primary}
                title="No communities yet"
                message="Communities are the smaller rooms inside this membership - a place to gather around one topic, with their own posts and members. The first one to be created shows up here."
              />
            ) : visibleGroups.length === 0 ? (
              /* Groups exist, the query hid them — so this state offers the way
                 back rather than just reporting the miss. */
              <EmptyState
                art="search"
                accent={primary}
                title="No communities match"
                message={groupQuery ? `Nothing here matches “${groupQuery}”. Try a shorter word, or clear the filters to see everything.` : "No community matches the filters you picked. Clear them to see everything in this membership."}
                action={{
                  label: "Clear search & filters",
                  onClick: () => { setGroupQuery(""); setTypeFilter("all"); setPriceFilter("all"); },
                }}
              />
            ) : (
              // All groups in one grid (joined first) — no separate near-empty
              // "Discover Groups" section sitting on its own row.
              <>
                <GroupSection
                  // The card header names the section on Classic/Studio/Catalog.
                  title={groupsRail ? null : "Communities"}
                  count={visibleGroups.length}
                  groups={pagedGroups}
                  gridClassName={
                    // Classic/Studio/Catalog sit beside the rail (2-up). Creator's
                    // main column fills the freed width, so it goes up to 3-up.
                    // The card is compact now, so the rail layout fits 3-up at xl
                    // too rather than capping non-creators at 2. Spotlight uses
                    // the same columns as its Courses page.
                    isSpotlight
                      ? SPOTLIGHT_GRID
                      : groupsRail
                      ? "grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3"
                      : undefined
                  }
                  primary={primary}
                  groupBusyId={groupBusyId}
                  onCardClick={onGroupClick}
                  onOpen={openGroup}
                  onInstructor={openInstructor}
                  onMembers={membersMenuEnabled ? (gt) => goMembers(gt) : null}
                />
                <Pagination
                  page={safeGroupPage}
                  totalPages={totalGroupPages}
                  primary={primary}
                  onChange={(p) => {
                    setGroupPage(p);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </>
            );

          const groupsBody = (
            <>
            {notice && (
              <div className="mb-4 text-sm rounded-xl px-3.5 py-2.5 bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-500/30">
                {notice}
              </div>
            )}

            {isSpotlight ? (
              // Spotlight: the same open page as its Courses — title, search and
              // filter, then Explore — rather than a card around the list.
              <>
                <SpotlightIntro
                  title="Community"
                  subtitle={`The communities inside ${community?.name || "this membership"} — join a conversation, or find your people.`}
                  search={groupsSearch}
                />
                <SpotlightExplore caption={`${visibleGroups.length} ${visibleGroups.length === 1 ? "community" : "communities"}${groupQuery || activeFilters ? " match" : ", all in one place."}`}>
                  {groupsList}
                </SpotlightExplore>
              </>
            ) : groupsRail ? (
              // Same card as Courses: header (title + count + search/filter) then
              // the grid inside, so the two sections read as one design.
              <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <SectionHeader
                  primary={primary}
                  iconPath="M4 5h6v6H4V5zm10 0h6v6h-6V5zM4 15h6v4H4v-4zm10 0h6v4h-6v-4z"
                  title="Communities"
                  subtitle="Communities in this membership"
                  trailing={`${visibleGroups.length} ${visibleGroups.length === 1 ? "community" : "communities"}`}
                  action={groupsSearch}
                />
                <div className="p-5 sm:p-6">{groupsList}</div>
              </div>
            ) : (
              <>
                {groupsSearch}
                {groupsList}
              </>
            )}
            </>
          );
          // Mirrors Courses / Events: cards left, membership rail right.
          if (!groupsRail) return groupsBody;
          return (
            // Fixed 22rem rail; on Creator it stacks below until xl (see tpl-rail-grid).
            <div className="tpl-rail-grid">
              <aside className="space-y-4 order-2 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
                <CommunityMiniBanner
                  community={community}
                  totalMembers={totalMembers}
                  totalGroups={groups.length}
                  className="h-44"
                />
                {member ? (
                  <MyPointsCard ready={!loading} me={myStanding} pts={myPointsSettings} token={token} groupToken={gid} primary={primary} member={member} />
                ) : (
                  // Guests have no standing to show — this is the hero's old CTA.
                  <button
                    onClick={() => openModal("join")}
                    style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                    className="w-full rounded-full px-7 py-3 font-bold text-white shadow-lg transition hover:opacity-90"
                  >
                    Join Community
                  </button>
                )}
                {/* Banner ads for the Community page — under the points card. */}
                <InlineBanner token={token} placement="community" wrapperClass="space-y-3" />
              </aside>
              <div className="order-1 min-w-0">
                {/* Events targeted to the Community page. */}
                <MockTestLauncher page="community" groupToken={null} funnelId={member?.funnel_id} memberId={member?.community_member_id} jwt={member?.member_token} primary={primary} />
              <UpcomingEventBanner token={token} placement="community" primary={primary} className="mb-6" />
                {groupsBody}
              </div>
            </div>
          );
        })())}
      </div>

      {/* Footer lives INSIDE the main column: Studio's root is `lg:flex`, so a
          footer left outside becomes a third flex item and lands to the right of
          the content instead of under it. Other templates use `contents` for this
          wrapper, so the position in flow is unchanged for them. */}
      {/* Phones render the member chat full-screen (100dvh−header); hide the
          footer there so it doesn't spill under the composer. Returns at sm+. */}
      <div className={section === "memberchat" ? "hidden sm:contents" : "contents"}>
        <PolicyFooter policies={community?.policies} token={token} communityName={community?.name} primary={primary} showBranding={community?.show_branding} />
      </div>
      </div>{/* /creator content column */}
      </div>{/* /main column */}

      {/* Creator uses the header hamburger + slide-in drawer on mobile (no bottom nav). */}

      {/* ---------- Auth modal ---------- */}
      {modal && (
        <AuthModal
          mode={modal}
          community={community}
          primary={primary}
          form={form}
          setForm={setForm}
          busy={busy}
          msg={msg}
          onClose={closeModal}
          onSwitch={openModal}
          onSubmitJoin={submitJoin}
          onSubmitSignin={submitSignin}
          forgotStep={forgotStep}
          onSubmitForgot={submitForgot}
        />
      )}

      <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />

      {/* ---------- Group join confirmation modal ---------- */}
      {joinGroup && (
        <GroupJoinModal
          group={joinGroup}
          primary={primary}
          busy={groupBusyId === joinGroup.group_id}
          message={joinMsg}
          onConfirm={confirmGroupJoin}
          onClose={() => {
            if (groupBusyId === joinGroup.group_id) return;
            setJoinGroup(null);
            setJoinMsg("");
          }}
        />
      )}

      {/* ---------- Profile modal ---------- */}
      {showMyPlans && member && (
        <MyPlansModal member={member} primary={primary} onClose={() => setShowMyPlans(false)} />
      )}
      {showProfile && member && (
        <ProfileModal
          token={token}
          member={member}
          groupToken={gid}
          primary={primary}
          banner={profileBanner(community)}
          onClose={() => setShowProfile(false)}
          onUpdated={saveMember}
          onPointsChange={refreshMyPoints}
        />
      )}

      {showChangePassword && member && (
        <ChangePasswordModal
          token={token}
          member={member}
          primary={primary}
          banner={profileBanner(community)}
          onClose={() => setShowChangePassword(false)}
        />
      )}

      {showAccountSettings && member && (
        <AccountSettingsModal
          token={token}
          member={member}
          primary={primary}
          banner={profileBanner(community)}
          onClose={() => setShowAccountSettings(false)}
        />
      )}

      {showMobileCode && member && (
        <MobileCodeModal
          code={token}
          primary={primary}
          onClose={() => setShowMobileCode(false)}
        />
      )}

      {/* ---------- Popup banner ad ---------- */}
      <BannerPopup token={token} placement={HOME_PLACEMENT[section] || "community"} />
    </div>
  );
}


/* ============================ Discover filter ============================ */

// A single radio-style row inside a filter column. Clicking the active option
// again clears it back to "All", so there's no separate "All" row to manage.
// Premium selectable chip used inside the filter groups.
function FilterChip({ label, selected, onSelect, primary, icon }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-all"
      style={
        selected
          ? { borderColor: primary, backgroundColor: `${primary}14`, color: primary, boxShadow: `0 1px 6px -2px ${primary}66` }
          : { borderColor: "rgb(var(--c-gray-200))", backgroundColor: "rgb(var(--c-surface))", color: "rgb(var(--c-gray-600))" }
      }
    >
      {selected ? (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        icon
      )}
      {label}
    </button>
  );
}

// One labelled group of filter chips (icon + heading + chips).
function FilterGroup({ icon, title, primary, children }) {
  return (
    <div>
      <div className="mb-2.5 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg" style={{ backgroundColor: `${primary}14`, color: primary }}>
          {icon}
        </span>
        <p className="text-[13px] font-bold text-gray-900">{title}</p>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

// Filter button + premium popover for the discover grid. Facets are grouped
// (Type, then Price) and chosen as chips; re-tapping a chip clears that facet.
function GroupFilter({ primary, open, setOpen, typeFilter, setTypeFilter, priceFilter, setPriceFilter, activeFilters }) {
  const pickType = (v) => setTypeFilter((cur) => (cur === v ? "all" : v));
  const pickPrice = (v) => setPriceFilter((cur) => (cur === v ? "all" : v));

  // The dropdown renders through a portal (fixed position, anchored to the
  // button) so it can never be clipped by an ancestor card's overflow-hidden —
  // which happened when the filtered grid was short/empty.
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const globeIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 000 18M12 3a14 14 0 010 18" />
    </svg>
  );
  const lockIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4M6 11h12a1 1 0 011 1v7a1 1 0 01-1 1H6a1 1 0 01-1-1v-7a1 1 0 011-1z" />
    </svg>
  );
  const tagIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M3 4v5.6a2 2 0 00.6 1.4l8 8a2 2 0 002.8 0l5.2-5.2a2 2 0 000-2.8l-8-8A2 2 0 0011.6 2H6a3 3 0 00-3 3z" />
    </svg>
  );
  const giftIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M3 8h18v4H3V8zm9 0S10 2 7 4s5 4 5 4zm0 0s2-6 5-4-5 4-5 4z" />
    </svg>
  );
  const dollarIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v20M16.5 7.5A4 4 0 0012.5 5h-1a3.5 3.5 0 000 7h1a3.5 3.5 0 010 7h-1a4 4 0 01-4-2.5" />
    </svg>
  );
  const levelIcon = (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.5a.56.56 0 011.04 0l2.13 4.32 4.77.69c.46.07.64.64.3.96l-3.45 3.36.82 4.75c.08.46-.4.81-.81.6L12 15.93l-4.28 2.25c-.41.21-.89-.14-.81-.6l.82-4.75L4.28 9.47a.56.56 0 01.3-.96l4.77-.69 2.13-4.32z" />
    </svg>
  );
  const clockIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-[50px] items-center gap-2 rounded-2xl border border-gray-200 bg-surface px-4 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
      >
        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
        </svg>
        <span className="hidden sm:inline">Filter</span>
        {activeFilters > 0 && (
          <span
            className="flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold text-white"
            style={{ background: primary, color: "var(--brand-btn-text, #fff)" }}
          >
            {activeFilters}
          </span>
        )}
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[9999] w-[360px] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-gray-100 bg-surface shadow-2xl ring-1 ring-gray-900/5"
            style={{ top: pos.top, right: pos.right }}
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-b from-gray-50/80 to-surface px-4 py-3">
              <div className="flex items-center gap-2">
                <svg className="h-4 w-4" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
                </svg>
                <p className="text-sm font-bold text-gray-900">Filters</p>
              </div>
              {activeFilters > 0 && (
                <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
                  {activeFilters} active
                </span>
              )}
            </div>

            {/* groups */}
            <div className="space-y-4 p-4">
              <FilterGroup icon={globeIcon} title="Type" primary={primary}>
                <FilterChip label="Public" icon={globeIcon} selected={typeFilter === "public"} onSelect={() => pickType("public")} primary={primary} />
                <FilterChip label="Private" icon={lockIcon} selected={typeFilter === "private"} onSelect={() => pickType("private")} primary={primary} />
              </FilterGroup>

              <div className="h-px bg-gray-100" />

              <FilterGroup icon={tagIcon} title="Price" primary={primary}>
                <FilterChip label="Free" icon={giftIcon} selected={priceFilter === "free"} onSelect={() => pickPrice("free")} primary={primary} />
                <FilterChip label="Paid" icon={dollarIcon} selected={priceFilter === "paid"} onSelect={() => pickPrice("paid")} primary={primary} />
                <FilterChip label="Free trial" icon={clockIcon} selected={priceFilter === "free_trial"} onSelect={() => pickPrice("free_trial")} primary={primary} />
                {/* Earned groups are a price band of their own: they match
                    neither Free nor Paid, so without this chip they could only
                    be found under "All". */}
                <FilterChip label="Unlocks at a level" icon={levelIcon} selected={priceFilter === "level"} onSelect={() => pickPrice("level")} primary={primary} />
              </FilterGroup>
            </div>

            {/* footer */}
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                disabled={activeFilters === 0}
                onClick={() => {
                  setTypeFilter("all");
                  setPriceFilter("all");
                }}
                className="text-xs font-semibold text-gray-500 transition hover:text-gray-700 disabled:opacity-40"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
              >
                Done
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

/* ============================ Groups ============================ */

/** Compact numbered pagination for the group list. Hidden when there's
 *  only a single page. Shows Prev / page numbers (with … ellipses) / Next. */

function GroupSection({ title, groups, count, emptyText, primary, groupBusyId, onCardClick, onOpen, onInstructor, onMembers, className = "", gridClassName = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" }) {
  return (
    <section className={className}>
      {/* Omitted when a card SectionHeader above already names the section. */}
      {title && (
        <h2 className="text-lg font-bold text-gray-900 mb-5">
          {title} <span className="text-gray-400 text-base">({count ?? groups.length})</span>
        </h2>
      )}
      {groups.length === 0 ? (
        <div className="text-center text-gray-500 bg-surface rounded-2xl border border-gray-200 shadow-sm py-12">
          {emptyText || "No communities here."}
        </div>
      ) : (
        <div className={gridClassName}>
          {groups.map((g) => (
            <GroupCard
              key={g.group_id}
              g={g}
              primary={primary}
              busy={groupBusyId === g.group_id}
              onClick={() => onCardClick(g)}
              onOpen={() => onOpen(g)}
              onInstructor={onInstructor}
              onMembers={onMembers}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* Cover-badge palette — the pricing badges reuse the exact scale CourseViews
   uses for a course tile's access badge (accessColor), so courses and groups
   read as one system.

   Joined is a fixed orange — STATIC, not the theme variable, so every community
   renders the same badge regardless of the primary they picked. Orange keeps it
   in the same warm family as the rest of the page while staying off the pricing
   scale, and a two-stop gradient from a bright to a burnt orange gives it the
   depth a flat fill was missing. Safe to sit this close to Trial's amber because
   a joined card never renders a pricing badge — the two are mutually exclusive
   on the same cover. */
const BADGE_FREE = "#16a34a";
/* Earned and included get their own colours — see the badge branch below. */
const BADGE_LEVEL = "#4f46e5";
const BADGE_COURSE = "#0284c7";
const BADGE_PAID = "#dc2626";
const BADGE_TRIAL = "#d97706";
/* Joined: bright orange → burnt orange, with a matching warm glow. */
const BADGE_JOINED_FROM = "#fb923c";
const BADGE_JOINED_TO = "#ea580c";
const BADGE_JOINED_GLOW = "rgba(234,88,12,0.55)";

function GroupCard({ g, primary, busy, onClick, onOpen, onInstructor, onMembers }) {
  // Pricing is separate from visibility: free | free_trial | paid.
  const pricing = g.pricing_type || "free";
  const isPaid = pricing === "paid";
  const isTrial = pricing === "free_trial";
  /*
   * EARNED or INCLUDED, which are neither free nor bought. This card showed a
   * green FREE badge on a group that opens at a gamification level, and on one
   * that comes with a course — both of which a member cannot simply join, so
   * "Free" reads as an invitation the gate then refuses.
   */
  const isLevelBadge = isLevelGroup(g);
  const isCourseBadge = isCourseLinkedGroup(g);
  const trialLapsed = isTrial && g.joined && g.trial_expired;
  // Bare amount (strip any "Free trial · " prefix) for the bottom-right price.
  const amountLabel = (g.price_label || "").replace(/^Free trial · /, "") || formatPrice(g.amt);
  const accessLabel = g.access_type === 2 ? "Private" : "Public";

  // Gated groups send the user to the offer that unlocks THIS card's group.
  const buyNow = () => window.open(withReferral(unlockUrlFor(g)), "_blank", "noopener,noreferrer");

  // Whole card is the action target (the reference design has button-less cards):
  // expired trial -> checkout, joined -> open the feed, pending -> no-op,
  // gated with an offer -> that offer's sales page, straight from the card,
  // otherwise (free / invite-only / active free-trial) -> the group landing page.
  const handleClick = () => {
    if (busy) return;
    if (g.instructor_access) return onOpen();
    if (trialLapsed) return buyNow();
    if (g.joined) return onOpen();
    if (g.pending) return;
    // The landing's only action for a gated group is "Get access", which opens
    // this same URL — so skip the stop. `locked` is the bridge's answer; paid is
    // the fallback for an older bridge. No offer means invite-only, and the
    // landing is where that gets said.
    const gated = g.locked ?? (g.access_mode === "entitlement" || isPaid);
    if (gated && g.unlock_product) return buyNow();
    onClick();
  };

  return (
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl bg-surface transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-[3px] ${
        g.joined
          ? ""
          : "shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)] ring-1 ring-gray-900/[0.06] hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_18px_38px_-16px_rgba(15,23,42,0.22)] hover:ring-gray-900/10"
      }`}
      style={
        g.joined
          ? {
              boxShadow: `inset 0 0 0 1px ${primary}33, 0 1px 2px rgba(15,23,42,0.04), 0 14px 32px -18px ${primary}4d`,
            }
          : undefined
      }
    >
      {/* Hairline of brand colour along the very top — a small signature that
          separates a joined card from a generic white rectangle. */}
      {g.joined && (
        <span
          className="absolute inset-x-0 top-0 z-10 h-[3px]"
          style={{ background: `linear-gradient(90deg, ${primary}, ${primary}66)` }}
        />
      )}
      {/* Banner — a fixed short strip rather than a 16:9 block. At 16:9 the cover
          alone ran over half the card's height; a letterbox keeps the image as an
          accent and lets the content lead. */}
      <div className="relative h-24 overflow-hidden bg-gray-100 sm:h-[104px]">
        <img
          src={groupBanner(g.banner)}
          alt={g.name}
          className={`h-full w-full object-cover transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.07]${
            isEmptyBanner(g.banner) ? " grayscale" : ""
          }`}
          onError={(e) => (e.currentTarget.src = GROUP_DEFAULT_BANNER)}
        />
        {/* Scrim — light touch, only enough to keep the status pill legible on a
            bright cover. No brand tile here: the group's identity is already the
            cover plus the title, and the tile was just repeating the initial. */}
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/20 via-transparent to-ink/10" />
        {/* Joined — same pill geometry as the course access badge, in a fixed
            orange gradient (see the palette note above). White text and check on
            the gradient, so it holds up over a light or dark cover alike. */}
        {g.joined && (
          <span
            className="absolute right-2.5 top-2.5 z-10 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white"
            style={{
              background: `linear-gradient(140deg, ${BADGE_JOINED_FROM}, ${BADGE_JOINED_TO})`,
              boxShadow: `0 6px 16px -6px ${BADGE_JOINED_GLOW}`,
            }}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Joined
          </span>
        )}
        {/* Pricing-type label (top-right) — hidden once joined so the single
            "Joined" pill owns that corner (matches the reference card). */}
        {!g.joined && (isLevelBadge || isCourseBadge ? (
          <span
            className="absolute right-2.5 top-2.5 z-10 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm"
            style={{ backgroundColor: isCourseBadge ? BADGE_COURSE : BADGE_LEVEL }}
          >
            <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              {isCourseBadge ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0v7m-5-4.5V12m10 4.5V12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.5a.56.56 0 011.04 0l2.13 4.32 4.77.69c.46.07.64.64.3.96l-3.45 3.36.82 4.75c.08.46-.4.81-.81.6L12 15.93l-4.28 2.25c-.41.21-.89-.14-.81-.6l.82-4.75L4.28 9.47a.56.56 0 01.3-.96l4.77-.69 2.13-4.32z" />
              )}
            </svg>
            <span>{groupLevelLabel(g)}</span>
          </span>
        ) : isPaid || isTrial ? (
          <span
            className="absolute right-2.5 top-2.5 z-10 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm"
            style={{ backgroundColor: isPaid ? BADGE_PAID : BADGE_TRIAL }}
          >
            {isPaid ? (
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v20M16.5 7.5A4 4 0 0012.5 5h-1a3.5 3.5 0 000 7h1a3.5 3.5 0 010 7h-1a4 4 0 01-4-2.5" />
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span>{isPaid ? "Paid" : "Trial"}</span>
            {amountLabel && <span className="font-extrabold">{amountLabel}</span>}
          </span>
        ) : (
          <span
            className="absolute right-2.5 top-2.5 z-10 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm"
            style={{ backgroundColor: BADGE_FREE }}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M3 8h18v4H3V8zm9 0S10 2 7 4s5 4 5 4zm0 0s2-6 5-4-5 4-5 4z" />
            </svg>
            Free
          </span>
        ))}
      </div>

      {/* Body.
          Composition note — the previous version put the brand tile, the title and
          then a wrapping row of three differently-coloured pills all in one stack.
          The pills were the problem: three competing fills at the same weight read
          as clutter and wrapped raggedly at 3-up. Here the tile lives inside the
          cover (so the text column has a single, unbroken left edge), and the meta
          collapses to ONE quiet line of muted text with dot separators. Only the
          action keeps colour, so there is exactly one thing asking to be clicked. */}
      <div
        className="relative flex flex-1 flex-col gap-1 p-4"
        style={g.joined ? { background: `linear-gradient(180deg, ${primary}08 0%, transparent 60%)` } : undefined}
      >
        <Tooltip label={g.name}>
          <h3 className="line-clamp-1 text-[15px] font-semibold leading-snug tracking-[-0.01em] text-gray-900">
            {g.name}
          </h3>
        </Tooltip>

        {/* Description — two lines. Fixed min-height ONLY here so titles and the
            footer line up across a row; empty descriptions still collapse. */}
        {g.description ? (
          <Tooltip label={g.description}>
            <p className="line-clamp-2 break-words text-[12.5px] leading-[1.55] text-gray-500">{g.description}</p>
          </Tooltip>
        ) : null}

        {/* Bottom line — this is where the old footer was. No Open button: the
            whole card is already the click target, so a CTA was a second control
            doing the same job. What the footer genuinely carried — who hosts the
            group and whether you are in it — moves up into one line here.

            Left: the host, as an avatar + name (the strongest signal, so it leads).
            Then the quiet facts, dot-separated. Right: membership state, the only
            coloured thing on the card. */}
        <div className="mt-auto flex flex-wrap items-center gap-x-1.5 gap-y-1.5 pt-3 text-[11.5px] text-gray-400">
          {busy ? (
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-500">
              <Spinner size={11} />
              Joining…
            </span>
          ) : (
            <>
              <span className="inline-flex items-center gap-1">
                {g.access_type === 2 ? (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
                  </svg>
                ) : (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 000 18M12 3a14 14 0 010 18" />
                  </svg>
                )}
                {accessLabel}
              </span>

              <span aria-hidden="true" className="text-gray-300 dark:text-gray-500">·</span>

              {onMembers ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onMembers(g.group_token); }}
                  title="View members"
                  className="rounded transition-colors hover:text-gray-700 hover:underline"
                >
                  {formatCount(g.member_count)} {g.member_count === 1 ? "member" : "members"}
                </button>
              ) : (
                <span>
                  {formatCount(g.member_count)} {g.member_count === 1 ? "member" : "members"}
                </span>
              )}
            </>
          )}

          {/* Owner ("hosted by") reads last in the meta row, after access and
              member count. */}
          {instructorsOf(g).length > 0 && (
            <>
              <span aria-hidden="true" className="text-gray-300 dark:text-gray-500">·</span>
              <InstructorChips
                list={instructorsOf(g)}
                onInstructor={onInstructor}
                primary={primary}
                prefix="Hosted by"
                prefixInLabel={false}
                maxAvatars={3}
                sizeClass="h-5 w-5 shrink-0"
                textClass="text-[9px]"
                imgClass="ring-1 ring-gray-900/10"
                initialClass="ring-1 ring-black/5"
                initialStyle={{ backgroundColor: `${primary}1f`, color: primary }}
                labelClass="truncate font-semibold text-gray-600 transition-colors group-hover/by:text-gray-900"
                className="group/by inline-flex min-w-0 max-w-[55%] items-center gap-1.5 text-left"
              />
            </>
          )}

          {/* Membership state — right-aligned so it reads as status, not as another
              fact in the list. Pending outranks joined; neither shows while busy. */}
          {!busy && g.pending ? (
            <span className="ml-auto inline-flex shrink-0 items-center gap-1 font-semibold text-amber-600 dark:text-amber-300">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.1">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Pending
            </span>
          ) : !busy && g.joined ? (
            <span className="ml-auto inline-flex shrink-0 items-center gap-1 font-semibold text-green-500">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              You're a member
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* Confirmation modal shown when a member taps a group they haven't joined.
   Free groups join + open instantly; private groups send an approval request. */
function GroupJoinModal({ group, primary, busy, message, onConfirm, onClose }) {
  const isPrivate = group.access_type === 2;
  /*
   * EARNED at a gamification level: no price, no join — the member reaches the
   * level, and the chip says so in the dashboard's own words.
   */
  const isLevelGate = isLevelGroup(group) || isCourseLinkedGroup(group);
  const levelText = groupLevelLabel(group);

  const initial = (group.name || "?").charAt(0).toUpperCase();

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-surface w-full h-full sm:h-auto sm:max-w-md rounded-none sm:rounded-2xl overflow-y-auto sm:overflow-hidden max-h-none sm:max-h-[90vh] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* banner */}
        <div className="h-36 bg-gray-100 overflow-hidden relative">
          <img
            src={groupBanner(group.banner)}
            alt={group.name}
            className={`w-full h-full object-cover${isEmptyBanner(group.banner) ? " grayscale" : ""}`}
            onError={(e) => (e.currentTarget.src = GROUP_DEFAULT_BANNER)}
          />
          <span
            className={`absolute top-2 right-2 text-[11px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${
              isPrivate ? "text-white bg-ink/80" : "bg-surface/95 shadow-sm"
            }`}
            style={isPrivate ? undefined : { color: primary }}
          >
            {/* An earned group says which level rather than "Free". */}
            {isLevelGate
              ? levelText
              : isPrivate
              ? "Private"
              : "Free"}
          </span>
          <button
            onClick={onClose}
            className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3">
            <span
              className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center text-white font-bold overflow-hidden"
              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
            >
              {initial}
            </span>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 line-clamp-1">{group.name}</h3>
              <p className="text-xs text-gray-500">
                {formatCount(group.member_count)} {group.member_count === 1 ? "Member" : "Members"} •{" "}
                {isPrivate ? "Private community" : "Free community"}
              </p>
            </div>
          </div>

          {group.description && (
            <p className="text-sm text-gray-500 mt-3 line-clamp-3">{group.description}</p>
          )}

          <div
            className="flex items-start gap-2 text-sm rounded-xl px-3.5 py-2.5 mt-3 font-medium"
            style={{ backgroundColor: `${primary}14`, color: primary, border: `1px solid ${primary}33` }}
          >
            <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              {isPrivate ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <span>
              {isPrivate
                ? "This is a private community. Your request will be sent to the admin for approval."
                : "This is a free community. Join now to get instant access to the feed."}
            </span>
          </div>

          {message ? (
            <div className="mt-4">
              <div
                className="flex items-start gap-2 text-sm rounded-xl px-3.5 py-2.5 font-medium"
                style={{ backgroundColor: `${primary}14`, color: primary, border: `1px solid ${primary}33` }}
              >
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {message}
              </div>
              <button
                onClick={onClose}
                style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                className="w-full text-white font-semibold py-3 rounded-xl mt-4 hover:opacity-90 transition"
              >
                Done
              </button>
            </div>
          ) : (
            <button
              onClick={onConfirm}
              disabled={busy}
              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
              className="w-full text-white font-semibold py-3 rounded-xl mt-5 hover:opacity-90 transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && (
                <Spinner size={16} />
              )}
              {busy ? "Please wait…" : isPrivate ? "Request to join" : "Join community"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact member-count formatting (e.g. 80100 -> "80.1k") to match the design.
function formatCount(n) {
  const num = Number(n) || 0;
  if (num >= 1000) {
    const k = num / 1000;
    return (k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")) + "k";
  }
  return String(num);
}

/* ============================ Small UI helpers ============================ */


export default MembershipHome;
